import { Component, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import { fromEvent, Observable, Subscription } from 'rxjs';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { PuzzleTimerComponent } from 'projects/tools/src/lib/shared-module/puzzle-timer/puzzle-timer.component';
import { PuzzleFrameworkService } from 'projects/tools/src/lib/puzzle-framework-module/puzzle-framework.service';
import { PuzzleFrameworkConfigModel } from 'projects/tools/src/lib/store/puzzle-framework-config.model';
import { HelperFunctionsService } from 'projects/tools/src/lib/shared-module/helpers/helper-functions.service';
import { Store } from '@ngxs/store';
import { v1 as uuidv1 } from 'uuid';
import { PuzzleGameState } from 'projects/tools/src/lib/store/puzzle-game.state';
import { PuzzleGameModel } from 'projects/tools/src/lib/store/puzzle-game.model';
import { StartGame } from 'projects/tools/src/lib/store/puzzle-game.actions';
import { PuzzleWelcomeDialogComponent } from 'projects/tools/src/lib/shared-module/puzzle-welcome-dialog/puzzle-welcome-dialog.component';
import { PuzzleWinDialogComponent } from 'projects/tools/src/lib/shared-module/puzzle-win-dialog/puzzle-win-dialog.component';
import { PuzzleWinDialogService } from '../../../../tools/src/lib/shared-module/puzzle-win-dialog/puzzle-win-dialog.service';
import { PuzzleSolutionWordDialogComponent } from '../../../../tools/src/lib/shared-module/puzzle-solution-word-dialog/puzzle-solution-word-dialog.component';
import { PuzzleStorageEngine } from '../puzzle-storage-engine.service';
import {
  AlertController,
  ModalController,
  IonFab,
  IonFabButton,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonProgressBar,
  IonText,
  IonToolbar,
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { trophySharp, arrowRedoSharp, bugSharp, codeWorking } from 'ionicons/icons';
import { PuzzleButtonBarComponent } from 'projects/web/src/app/puzzle-button-bar/puzzle-button-bar.component';
import { CrosswordBoardComponent } from 'projects/tools/src/lib/crossword-module/crossword-board/crossword-board.component';
import { DefinitionBarComponent } from 'projects/tools/src/lib/shared-module/definition-bar/definition-bar.component';
import { ImagePuzzleBoardComponent } from 'projects/tools/src/lib/image-puzzle-module/image-puzzle-board/image-puzzle-board.component';
import { LeaderboardInfoComponent } from 'projects/tools/src/lib/shared-module/leaderboard-info/leaderboard-info.component';
import { PrintInfoComponent } from 'projects/tools/src/lib/shared-module/print-info/print-info.component';
import { PuzzleMoveCounterComponent } from 'projects/tools/src/lib/shared-module/puzzle-move-counter/puzzle-move-counter.component';
import { QuizBoardComponent } from 'projects/tools/src/lib/quiz-module/board/quiz-board.component';
import { SudokuBoardComponent } from 'projects/tools/src/lib/sudoku-module/board/sudoku-board.component';
import { WordSearchPuzzleBoardComponent } from 'projects/tools/src/lib/word-search-puzzle-module/board/word-search-puzzle-board.component';
import { PuzzleLandscapeMessageComponent } from 'projects/tools/src/lib/shared-module/puzzle-landscape-message/puzzle-landscape-message.component';
import { HashiBoardComponent } from 'projects/tools/src/lib/hashi-module/board/hashi-board.component';

import { CrosswordBoardConfig } from '../../../../tools/src/lib/crossword-module/crossword-board/crossword-board.component';
import { DeviceDetectorService } from 'ngx-device-detector';
import { PuzzleKeyboardService } from '../../../../tools/src/lib/shared-module/puzzle-softkeyboard/puzzle-keyboard.service';
import { LeaderboardService } from 'projects/tools/src/lib/shared-module/leaderboard/leaderboard.service';
import { FeedbackService } from 'projects/tools/src/lib/shared-module/feedback/feedback.service';
import { PuzzleTypeIdEnum } from '../../../../tools/src/lib/puzzle-framework-module/puzzle-type-id.enum';
import { SetConsent } from '../../../../tools/src/lib/store/puzzle-analytics-consent.actions';
import { ImaAdContainerDirective } from '../../../../tools/src/lib/shared-module/ima-ad/ima-ad-container.directive';

export interface GridTile {
  cols: number;
  rows: number;
  type: string;
}

@Component({
  selector: 'app-puzzle-screen',
  templateUrl: './puzzle-screen.component.html',
  styleUrls: ['./puzzle-screen.component.scss'],
  standalone: true,
  imports: [
    IonFab,
    IonFabButton,
    IonHeader,
    IonIcon,
    IonItem,
    IonLabel,
    IonList,
    IonProgressBar,
    IonText,
    IonToolbar,
    PuzzleButtonBarComponent,
    CrosswordBoardComponent,
    DefinitionBarComponent,
    ImagePuzzleBoardComponent,
    LeaderboardInfoComponent,
    PrintInfoComponent,
    PuzzleMoveCounterComponent,
    PuzzleTimerComponent,
    QuizBoardComponent,
    SudokuBoardComponent,
    WordSearchPuzzleBoardComponent,
    HashiBoardComponent,
    PuzzleLandscapeMessageComponent,
    TranslateModule,
    CommonModule,
    ImaAdContainerDirective,
  ],
})
export class PuzzleScreenComponent implements OnDestroy {
  private config: PuzzleFrameworkConfigModel;
  colSums: number;
  private layoutSize = 'default';

  initialized = false;

  crosswordBoardConfig: CrosswordBoardConfig;

  gameType: string;
  copyright: string;

  finished: Observable<boolean>;
  progress: number;

  tiles: GridTile[] = [];

  @ViewChild(PuzzleTimerComponent)
  timer: PuzzleTimerComponent;

  // windowHeight: number;

  isIframe = false;
  isMobile = false;
  prohibitLandscape = false;
  showTimer = false;
  showMoveCounter = false;

  private langSubscription: any;

  private puzzleIDSubscription;
  private lockID: string;
  private puzzleID: string;

  private localStorage$: Observable<Event>;
  private readonly localStorageSubscription: Subscription;
  private multiTabDialogOpen = false;

  private puzzleFrameworkConfig: PuzzleFrameworkConfigModel;
  private puzzleFrameworkConfigSubscription: Subscription;

  puzzleGame: PuzzleGameModel;
  private puzzleGame$: Observable<PuzzleGameModel>;
  private puzzleGameSubscription: Subscription;
  private puzzleFinished: boolean;
  private puzzleStarted: boolean;
  private dialogOpen: boolean;
  private winDialogOpen = false;
  allowMobileVerticalTopBar: boolean;

  iconFontLoaded = false;
  private solutionWordFound: boolean;
  private solutionWord: string;
  private noLocalStorage = false;
  swedishPuzzleLegacy: boolean;
  private sweepStakeModal: HTMLIonModalElement;

  removeSideMargins = false;
  private puzzleType: PuzzleTypeIdEnum;
  private sayingFound: boolean;
  backToWinMouseOver: boolean;
  // TODO make this an observable to be notified when the silentCheatCode is changed
  cheatCodeEnabled: boolean;
  noPuzzle = false;

  constructor(
    private router: Router,
    public framework: PuzzleFrameworkService,
    private helperFunctions: HelperFunctionsService,
    private store: Store,
    private translateService: TranslateService,
    private winDialogService: PuzzleWinDialogService,
    private storageEngine: PuzzleStorageEngine,
    private ionicModal: ModalController,
    private ionicAlert: AlertController,
    private el: ElementRef,
    private device: DeviceDetectorService,
    private keyboardService: PuzzleKeyboardService,
    public leaderboard: LeaderboardService,
    public feedback: FeedbackService,
    private route: ActivatedRoute
  ) {
    addIcons({
      trophySharp,
      arrowRedoSharp,
      bugSharp,
      codeWorking,
    });
    this.noLocalStorage = !this.helperFunctions.localStorageAvailable();
    if (this.noLocalStorage) {
      this.framework.showBrowserWarningBadge();
    }

    this.iconFontLoaded = true; // just set to true at first

    // this.framework.virtualKeyboardOpen.subscribe((open) => {
    //   window.setTimeout(() => {
    //     this.keyboardMarginBottom = open ? this.helperFunctions.getKeyboardHeight() : 0;
    //   }, 1);
    // });

    this.router.events.subscribe((event) => {
      if (!(event instanceof NavigationStart)) {
        return;
      }

      this.framework.checkHashParametersAndReload();

      this.framework.checkGdprParametersAndReload();

      // console.log(event, event instanceof NavigationStart);
      // Config.checkPuzzleNameAndReload();
    });

    this.isIframe = this.helperFunctions.isInIframe();
    this.isMobile = this.helperFunctions.isMobileDevice();

    if (this.framework.initialized) {
      this.initPuzzleScreen(this.framework.puzzleFrameworkConfig);
    }

    this.cheatCodeEnabled = this.framework.cheatCodeEnabled();

    this.framework.puzzleConfigLoaded$.subscribe((puzzleTypeId) => {
      this.puzzleType = puzzleTypeId;

      if (puzzleTypeId === null) {
        return;
      }

      if (puzzleTypeId === PuzzleTypeIdEnum.ERROR) {
        this.noPuzzle = true;
        return;
      }

      this.removeSideMargins = this.framework.puzzleFrameworkConfig.removeSideMargins;
      this.showTimer = this.framework.puzzleFrameworkConfig ? this.framework.puzzleFrameworkConfig.showTimer : false;
      this.showMoveCounter = this.framework.puzzleFrameworkConfig
        ? this.framework.puzzleFrameworkConfig.showMoveCounter
        : false;

      switch (puzzleTypeId) {
        case PuzzleTypeIdEnum.CROSSWORD_1:
        case PuzzleTypeIdEnum.CROSSWORD_20:
        case PuzzleTypeIdEnum.CROSSWORD_265:
        case PuzzleTypeIdEnum.CROSSWORD_221:
        case PuzzleTypeIdEnum.CROSSWORD_252:
        case PuzzleTypeIdEnum.CROSSWORD_310:
        case PuzzleTypeIdEnum.CROSSWORD_770:
        case PuzzleTypeIdEnum.CROSSWORD_771:
          this.crosswordBoardConfig = this.getCrosswordBoardConfig();
          this.crosswordBoardConfig.configPreset = puzzleTypeId;
          this.initPuzzleScreen(this.framework.puzzleFrameworkConfig);
          this.gameType = 'crossword';
          break;
        case PuzzleTypeIdEnum.IMAGE_PUZZLE:
          this.initPuzzleScreen(this.framework.puzzleFrameworkConfig);
          this.gameType = 'image';
          break;
        case PuzzleTypeIdEnum.SUDOKU_900:
          this.initPuzzleScreen(this.framework.puzzleFrameworkConfig);
          this.gameType = 'sudoku';
          break;
        case PuzzleTypeIdEnum.WORD_SEARCH_600:
          this.initPuzzleScreen(this.framework.puzzleFrameworkConfig);
          this.gameType = 'word-search';
          break;
        case PuzzleTypeIdEnum.QUIZ_1000:
          this.initPuzzleScreen(this.framework.puzzleFrameworkConfig);
          this.gameType = 'quiz';
          break;
        case PuzzleTypeIdEnum.HASHI:
          this.initPuzzleScreen(this.framework.puzzleFrameworkConfig);
          this.gameType = 'hashi';
          break;
      }
    });

    this.puzzleIDSubscription = this.store
      .select((state) => state.puzzleID)
      .subscribe((puzzleID: string) => {
        // Save PuzzleID and lock the puzzle to prevent multiple opened tabs
        this.puzzleID = puzzleID;

        if (!this.puzzleID || !this.puzzleID.length) {
          return;
        }

        this.lockPuzzle(puzzleID);

        this.checkAndRequestCookieConsentIfRequired().then((userClickedContinue) => {
          /**
           * Take care that Swedish Puzzle is initialized, also subscribe to the current puzzle GameState
           */
          this.puzzleGame$ = this.store.select(PuzzleGameState.getByID(puzzleID)) as Observable<PuzzleGameModel>;
          if (this.puzzleGameSubscription) {
            this.puzzleGameSubscription.unsubscribe();
          }

          this.puzzleGameSubscription = this.puzzleGame$.subscribe((puzzleGame) => {
            if (puzzleGame) {
              this.puzzleGame = puzzleGame;
              this.progress = puzzleGame.progress;

              if (this.puzzleFrameworkConfig && this.puzzleFrameworkConfig.solutionWordConfig.solutionDialogEnabled) {
                if (puzzleGame.sayingFound !== this.sayingFound) {
                  if (this.sayingFound !== undefined && puzzleGame.sayingFound && !puzzleGame.finished) {
                    this.showSweepstakeDialog(true);
                  }
                  this.sayingFound = puzzleGame.sayingFound;
                }

                if (puzzleGame.solutionFound !== this.solutionWordFound) {
                  if (this.solutionWordFound !== undefined && puzzleGame.solutionFound && !puzzleGame.finished) {
                    // console.log('showSweepstakeDialog: yes');
                    this.showSweepstakeDialog();
                  }
                  this.solutionWordFound = puzzleGame.solutionFound;
                }
              }

              if (this.solutionWord !== puzzleGame.solutionWord) {
                this.solutionWord = puzzleGame.solutionWord;
              }

              /**
               * When finished state changes, check if game is won and show swedish-win-screen
               */
              if (this.puzzleGame.finished !== this.puzzleFinished && this.puzzleGame.finished === true) {
                this.showWinDialog();
                /**
                 * When started state changes and new state is false and old state is undefined, show welcome dialog
                 */
              } else if (this.puzzleGame.started !== this.puzzleStarted) {
                // Check for warnings
                const hasWarning =
                  !helperFunctions.localStorageAvailable() ||
                  this.helperFunctions.unsupportedBrowser(
                    this.framework.hasKeyboardInput,
                    this.framework.puzzleFrameworkConfig.nativeKeyboard
                  ) ||
                  helperFunctions.isMobileDesktopMode();

                // if started changes from undefined or true to false, show welcome dialog
                if (
                  this.puzzleGame.started === false &&
                  (this.puzzleStarted === undefined || this.puzzleStarted === true)
                ) {
                  if (!this.puzzleFrameworkConfig.hideWelcomeDialog || hasWarning) {
                    this.showWelcomeDialog();
                  } else {
                    this.store.dispatch(new StartGame({ puzzleID: this.puzzleID }));
                  }
                  // if started changes from undefined to true, show return dialog
                } else if (
                  this.puzzleGame.started === true &&
                  this.puzzleStarted === undefined &&
                  !userClickedContinue
                ) {
                  if (!this.puzzleFrameworkConfig.hideWelcomeBackDialog || hasWarning) {
                    this.showReturnDialog();
                  }
                }
              }

              this.puzzleFinished = this.puzzleGame.finished;
              this.puzzleStarted = this.puzzleGame.started;
            }
          });
        });
      });

    this.puzzleFrameworkConfigSubscription = this.store
      .select((state) => state.puzzleFrameworkConfig)
      .subscribe((puzzleFrameworkConfig) => {
        this.puzzleFrameworkConfig = puzzleFrameworkConfig;
      });

    this.localStorage$ = fromEvent(window, 'storage');
    this.localStorageSubscription = this.localStorage$.subscribe((event: StorageEvent) => {
      this.checkIfSamePuzzleIsOpenedInMultipleTabsAndOpenDialog(event);
    });
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeyDown(event: KeyboardEvent) {
    // do not preventDefault if the win dialog is open - this is important
    // for highscore nickname entry
    if (!this.winDialogOpen && !this.feedback.dialogIsOpen) {
      this.keyboardService.onDocumentKeyDown(event);
    }
  }

  @HostListener('document:keyup', ['$event'])
  onDocumentKeyUp(event: KeyboardEvent) {
    this.keyboardService.onDocumentKeyUp(event);
  }

  @HostListener('window:keydown', ['$event'])
  onWindowKeyDown(event: KeyboardEvent): void {
    this.keyboardService.onWindowKeyDown(event);
  }

  private async checkIfSamePuzzleIsOpenedInMultipleTabsAndOpenDialog(event: StorageEvent) {
    if (event.key !== 'puzzleLock' || this.multiTabDialogOpen) {
      // exit early when event key is not puzzleLock or multi-tab warning is already opened
      return;
    }

    const lockTable = JSON.parse(event.newValue);
    if (!this.puzzleID || !lockTable.hasOwnProperty(this.puzzleID) || lockTable[this.puzzleID] === this.lockID) {
      // check if a lock ID exists for this puzzle and check if the lock id is identical. If so, exit early because this
      // instance has locked the puzzle itself
      return;
    }

    // The puzzle is locked by another instance. We should pause the game and show a lock dialog:
    if (this.leaderboard.canPauseSilently()) {
      this.framework.pauseCurrentGame();
    }
    this.multiTabDialogOpen = true;

    const translated = await Promise.all([
      this.translateService.get('PUZZLE.multiple_tab_dialog.title').toPromise(),
      this.translateService.get('PUZZLE.multiple_tab_dialog.text').toPromise(),
      this.translateService.get('PUZZLE.multiple_tab_dialog.button').toPromise(),
    ]);

    const alert = await this.ionicAlert.create({
      header: translated[0],
      message: translated[1],
      cssClass: 'puzzle-alert',
      backdropDismiss: false,
      buttons: [
        {
          text: translated[2],
          cssClass: 'primary',
        },
      ],
    });

    await alert.present();
    await alert.onDidDismiss();

    window.location.reload();
  }

  private async checkAndRequestCookieConsentIfRequired() {
    // Are we allowed to skip consent?
    const consentSkippable = this.puzzleFrameworkConfig.consentSkippable;

    // Skip consent only if BOTH flags are enabled in config
    if (consentSkippable && this.puzzleFrameworkConfig.skipConsent === true) {
      this.store.dispatch(new SetConsent());
      return false;
    }

    // Do we want to skip consent via URL parameter.
    let skipConsent = false;

    // If we are allowed to skip consent, check if we want to.
    if (consentSkippable) {
      // If consent parameter is set to true, use it
      this.route.fragment.subscribe((fragment) => {
        const wantsToSkipConsent = new URLSearchParams(fragment).get('skipConsent');
        if (wantsToSkipConsent === 'true') {
          // Set consent and record to skip asking for it
          this.store.dispatch(new SetConsent());
          skipConsent = true;
        }
      });
    }

    // If consent has been given in the past, or should be skipped
    if ((await this.store.selectOnce((storeInput) => storeInput.cookieConsent).toPromise()) || skipConsent) {
      return false;
    }

    await this.showWelcomeDialog();

    return true;
  }

  private async getWelcomeDialogTranslationStrings(): Promise<{ title: string; text: string }> {
    let translationStringPrefix = '';
    const devicePrefix = this.device.deviceType + '.';

    translationStringPrefix += this.framework.getTranslationKey();
    translationStringPrefix += '.welcome_dialog.';

    // Try to get device specific versions of the strings.
    let translationStrings = {
      title: this.translateService.get(translationStringPrefix + devicePrefix + 'title').toPromise(),
      text: this.translateService.get(translationStringPrefix + devicePrefix + 'text').toPromise(),
    };
    let translated = await Promise.all(Object.values(translationStrings));

    // Return them if they exist. If the strings do not exist the keys are returned, so we can check for the index.
    if (
      translated[0].indexOf(translationStringPrefix) === -1 &&
      translated[1].indexOf(translationStringPrefix) === -1
    ) {
      return {
        title: translated[0],
        text: translated[1],
      };
    }

    // Otherwise get the non device specific versions of the strings.
    translationStrings = {
      title: this.translateService.get(translationStringPrefix + 'title').toPromise(),
      text: this.translateService.get(translationStringPrefix + 'text').toPromise(),
    };
    translated = await Promise.all(Object.values(translationStrings));

    return {
      title: translated[0],
      text: translated[1],
    };
  }

  private async showSweepstakeDialog(isSayingDialog = false) {
    this.sweepStakeModal = await this.ionicModal.create({
      component: PuzzleSolutionWordDialogComponent,
      backdropDismiss: true,
      cssClass: 'puzzle-modal',
      componentProps: {
        sweepstake: this.puzzleFrameworkConfig.solutionWordConfig.sweepstake.enabled,
        winType: this.puzzleFrameworkConfig.solutionWordConfig.sweepstake.winType,
        isSayingDialog,
      },
    });

    if (this.leaderboard.canPauseSilently()) {
      this.framework.pauseCurrentGame();
    }
    await this.sweepStakeModal.present();
    // When the dialog is closed we get his answer.
    const userWantsToApply = await this.sweepStakeModal.onDidDismiss();
    this.sweepStakeModal = undefined;
    this.framework.resumeCurrentGame();

    // When the dialog was closed and the user wants to apply the action (take part in sweepstake), we do it here.
    // If targetBlank is false, we don't close the dialog, so we won't reach this section.
    if (userWantsToApply.data) {
      this.framework.sendSolutionWordData(this.solutionWord);
      await this.framework.openPauseDialog();
    }
  }

  private async hideSweepstakeDialog() {
    if (!this.sweepStakeModal) {
      return;
    }
    await this.sweepStakeModal.dismiss();
    await this.helperFunctions.timeout(500);
    this.sweepStakeModal = undefined;
  }

  private async showReturnDialog() {
    const resolved = await Promise.all([
      this.translateService.get('PUZZLE.return_dialog.title').toPromise(),
      this.translateService.get('PUZZLE.return_dialog.message').toPromise(),
      this.translateService.get('PUZZLE.return_dialog.button').toPromise(),
    ]);

    this.dialogOpen = true;
    const modal = await this.ionicModal.create({
      component: PuzzleWelcomeDialogComponent,
      backdropDismiss: false,
      cssClass: 'puzzle-modal return-dialog',
      componentProps: {
        started: false,
        title: resolved[0],
        text: resolved[1],
      },
    });

    if (this.leaderboard.canPauseSilently()) {
      this.framework.pauseCurrentGame();
    }
    await modal.present();
    await modal.onDidDismiss();

    this.framework.resumeCurrentGame();
    this.dialogOpen = false;
    this.store.dispatch(new StartGame({ puzzleID: this.puzzleID }));
  }

  private isInput(el) {
    const tagName = el && el.tagName && el.tagName.toLowerCase();
    return (
      (tagName === 'input' && el.type !== 'button' && el.type !== 'radio' && el.type !== 'checkbox') ||
      tagName === 'textarea'
    );
  }

  public async showWinDialog(immediate = false) {
    // Take care sweepstakedialog is hidden
    if (this.winDialogOpen) {
      return;
    }
    this.winDialogOpen = true;

    await this.hideSweepstakeDialog();

    if (!immediate) {
      await this.helperFunctions.timeout(this.winDialogService.solutionDialogDelay);
    }

    const modal = await this.ionicModal.create({
      component: PuzzleWinDialogComponent,
      cssClass: 'puzzle-modal',
      backdropDismiss: false,
    });

    this.framework.pauseCurrentGame();

    await modal.present();
    await modal.onDidDismiss();

    this.framework.resumeCurrentGame();
    this.winDialogOpen = false;
  }

  private async showWelcomeDialog() {
    if (this.dialogOpen) {
      return;
    }
    this.dialogOpen = true;
    const strings = await this.getWelcomeDialogTranslationStrings();

    const modal = await this.ionicModal.create({
      component: PuzzleWelcomeDialogComponent,
      backdropDismiss: false,
      cssClass: 'puzzle-modal welcome-dialog',
      componentProps: {
        started: false,
        title: strings.title,
        text: strings.text,
      },
    });

    this.framework.pauseCurrentGame();
    await modal.present();
    const result = await modal.onDidDismiss();
    this.framework.resumeCurrentGame();
    this.dialogOpen = false;

    this.store.dispatch(new StartGame({ puzzleID: this.puzzleID }));
  }

  @HostListener('window:blur')
  async onDocumentBlur() {
    if (
      this.framework.cheatCodeEnabled() ||
      this.puzzleFrameworkConfig.pauseOnBlurDisabled ||
      !this.leaderboard.canPauseSilently()
    ) {
      return;
    }
    this.framework.openPauseDialog();
  }

  @HostListener('touchmove', ['$event'])
  preventPullDownRefresh(event: KeyboardEvent) {
    let target = event.target as HTMLElement;
    while (target !== this.el.nativeElement) {
      // If we don't have a target, we prevent an undefined error.
      if (target == null) {
        return;
      }
      // prevent default if the event is not bubbling through NGX-SIMPLEBAR
      if (target.tagName === 'NGX-SIMPLEBAR') {
        return;
      }
      target = target.parentNode as HTMLElement;
    }
    event.preventDefault();
  }

  /**
   * Lock the puzzle with the given puzzleID. That means, that an entry in puzzleLock is made
   * with the puzzleID as key, and a newly generated uuid for this window as value.
   *
   * @param puzzleID
   * @private
   */
  private lockPuzzle(puzzleID: string) {
    if (this.noLocalStorage) {
      return;
    }
    this.lockID = uuidv1();
    // TODO replace with storageEngine.getItem()?
    const lockTable = JSON.parse(localStorage.getItem('puzzleLock') || '{}');
    lockTable[puzzleID] = this.lockID;
    this.storageEngine.setItem('puzzleLock', JSON.stringify(lockTable));
    // localStorage.setItem('puzzleLock', JSON.stringify(lockTable));
  }

  /** Initialize puzzleScreen
   *
   * @param config the configuration of the puzzleFramework
   */
  private initPuzzleScreen(config: PuzzleFrameworkConfigModel) {
    this.swedishPuzzleLegacy = config.swedishPuzzleLegacy;

    this.config = config;
    this.allowMobileVerticalTopBar = config.allowMobileVerticalTopBar && this.helperFunctions.isMobileDevice();
    this.initialized = true;
  }

  configureGrid(): void {
    this.colSums = this.config.layout[this.layoutSize].width;
    this.tiles = [];
    for (const tile of this.config.layout[this.layoutSize].tiles) {
      this.tiles.push(tile);
    }
  }

  ngOnDestroy(): void {
    if (this.langSubscription) {
      this.langSubscription.unsubscribe();
    }
    if (this.localStorageSubscription) {
      this.localStorageSubscription.unsubscribe();
    }
  }

  /**
   * Prevent double click zoom on ios devices
   */
  appClicked($event: MouseEvent) {
    $event.preventDefault();
  }

  private getCrosswordBoardConfig(): CrosswordBoardConfig {
    const {
      definFields,
      crosswordSidebar,
      fieldNumbers,
      splitWordList,
      splitDefinList,
      definitionBar,
      skipPredefined,
      constantAutoZoomDimensions,
      showWordLengths,
    } = this.puzzleFrameworkConfig.crosswordBoardConfig;
    return {
      definFields,
      crosswordSidebar,
      fieldNumbers,
      splitWordList,
      splitDefinList,
      definitionBar,
      skipPredefined,
      constantAutoZoomDimensions,
      showWordLengths,
    };
  }

  async showErrors() {
    const alert = await this.ionicAlert.create({
      header: 'Errors',
      message: this.framework.puzzleFileLoadErrors.join('<br/>'),
      cssClass: 'puzzle-alert',
      backdropDismiss: true,
    });

    await alert.present();
    await alert.onDidDismiss();
  }

  async showDeveloperMode() {
    await this.framework.showTranslatedToast('PUZZLE.developer_mode');
  }
}
